/**
 * Reset Password Page Component
 * Reset password using token from email link
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { authService } from '../../services/authService';
import { useSnackbar } from 'notistack';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Check if token exists
  useEffect(() => {
    if (!token) {
      setError('Token de recuperação não encontrado. Solicite um novo link de recuperação.');
    }
  }, [token]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.newPassword) {
      errors.newPassword = 'Nova senha é obrigatória';
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = 'A senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      errors.newPassword = 'A senha deve conter letras maiúsculas, minúsculas e números';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!token) {
      setError('Token inválido. Solicite um novo link de recuperação.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword({
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccess(true);
      enqueueSnackbar('Senha redefinida com sucesso!', { variant: 'success' });
    } catch (err: any) {
      const errorMessage = err.message ?? 'Erro ao redefinir senha. O link pode ter expirado.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (error) {
      setError(null);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Invalid token state
  if (!token && !loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Link Inválido
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              O link de recuperação é inválido ou expirou. Por favor, solicite um novo link de
              recuperação de senha.
            </Typography>
            <Button
              component={Link}
              to="/forgot-password"
              variant="contained"
              fullWidth
              sx={{ mb: 2 }}
            >
              Solicitar Novo Link
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="text"
              startIcon={<ArrowBack />}
              fullWidth
            >
              Voltar ao Login
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
          }}
        >
          {success ? (
            // Success state
            (<Box sx={{ textAlign: 'center' }}>
              <CheckCircle
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Senha Redefinida!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Sua senha foi alterada com sucesso. Você já pode fazer login com sua nova senha.
              </Typography>
              <Button variant="contained" fullWidth onClick={handleGoToLogin}>
                Ir para Login
              </Button>
            </Box>)
          ) : (
            // Form state
            (<>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
                  Redefinir Senha
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Digite sua nova senha
                </Typography>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  label="Nova Senha"
                  value={formData.newPassword}
                  onChange={handleInputChange('newPassword')}
                  error={!!formErrors.newPassword}
                  helperText={
                    formErrors.newPassword ||
                    'Mínimo 8 caracteres com letras maiúsculas, minúsculas e números'
                  }
                  disabled={loading}
                  autoComplete="new-password"
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirmar Nova Senha"
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  disabled={loading}
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Redefinir Senha'}
                </Button>

                <Button
                  component={Link}
                  to="/login"
                  fullWidth
                  variant="text"
                  startIcon={<ArrowBack />}
                  disabled={loading}
                >
                  Voltar ao Login
                </Button>
              </Box>
            </>)
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
